import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Globe, Clock, DollarSign, Calendar, Save, Check, Languages, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { ComboSelect } from '../components/shared/ComboSelect';
import { CURRENCIES, TIMEZONES, LANGUAGES, COUNTRIES, DATE_FORMATS, FISCAL_MONTHS } from '../data/worldData';

const API = process.env.REACT_APP_BACKEND_URL;

// Build option arrays for ComboSelect
const currencyOptions = CURRENCIES.map(c => ({ value: c.code, label: `${c.code} — ${c.name} (${c.symbol})`, secondary: c.symbol }));
const timezoneOptions = TIMEZONES.map(tz => ({ value: tz.value, label: tz.label }));
const languageOptions = LANGUAGES.map(l => ({ value: l.code, label: `${l.name} — ${l.native}`, secondary: l.native }));
const countryOptions = COUNTRIES.map(c => ({ value: c.code, label: c.name }));
const dateFormatOptions = DATE_FORMATS.map(d => ({ value: d.value, label: d.label }));
const fiscalOptions = FISCAL_MONTHS.map(m => ({ value: m, label: m.charAt(0).toUpperCase() + m.slice(1) }));

const SelectField = ({ label, value, onChange, options, testId }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1.5">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-colors"
      data-testid={testId}
    >
      {options.map((opt) => (
        <option key={typeof opt === 'string' ? opt : opt.value} value={typeof opt === 'string' ? opt : opt.value}>
          {typeof opt === 'string' ? opt : opt.label}
        </option>
      ))}
    </select>
  </div>
);

export default function GlobalSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/settings/global`);
      setSettings(data);
    } catch {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/settings/global`, settings);
      setSettings(data);
      setSaved(true);
      toast.success('Global settings saved');
      setTimeout(() => setSaved(false), 2000);
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (path, value) => {
    setSettings(prev => {
      const copy = { ...prev };
      if (path.includes('.')) {
        const [parent, child] = path.split('.');
        copy[parent] = { ...copy[parent], [child]: value };
      } else {
        copy[path] = value;
      }
      return copy;
    });
    setSaved(false);
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fadeIn" data-testid="global-settings-loading">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-gray-100 rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  const currency = settings?.currency || { code: 'USD', symbol: '$', position: 'before', decimal_places: 2 };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="global-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
            <Globe className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary" data-testid="global-settings-title">
              Global Settings<PageInfoTooltip pageKey="global-settings" />
            </h1>
            <p className="text-[11px] text-gray-medium">
              Configure your organization's regional and localization preferences
            </p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-emerald-brand hover:bg-emerald-600" data-testid="save-global-settings-btn">
          {saved ? <><Check className="h-4 w-4 mr-1.5" /> Saved</> : saving ? 'Saving...' : <><Save className="h-4 w-4 mr-1.5" /> Save Changes</>}
        </Button>
      </div>

      {/* Country & Region */}
      <Card className="bg-white border-gray-border" data-testid="country-settings">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-rose-600" />
            <CardTitle className="text-sm font-semibold">Country & Region</CardTitle>
            <Badge className="text-[9px] bg-blue-50 text-blue-600">Global</Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ComboSelect
              label="Country"
              value={settings?.country || ''}
              onChange={(v) => updateField('country', v)}
              options={countryOptions}
              placeholder="Search countries..."
              testId="country-select"
            />
            <ComboSelect
              label="Primary Language"
              value={settings?.language || 'en'}
              onChange={(v) => updateField('language', v)}
              options={languageOptions}
              placeholder="Search languages..."
              testId="language-select"
            />
          </div>
          {settings?.country && (
            <div className="mt-3 p-2.5 rounded-lg bg-gray-50 border border-gray-100 flex items-center gap-2">
              <span className="text-lg">{countryCodeToFlag(settings.country)}</span>
              <span className="text-sm text-text-primary font-medium">{COUNTRIES.find(c => c.code === settings.country)?.name}</span>
              <span className="text-xs text-gray-medium ml-1">({settings.country})</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Currency Settings */}
      <Card className="bg-white border-gray-border" data-testid="currency-settings">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-emerald-600" />
            <CardTitle className="text-sm font-semibold">Currency</CardTitle>
            <Badge className="text-[9px] bg-emerald-50 text-emerald-600">{CURRENCIES.length} currencies</Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ComboSelect
              label="Currency Code"
              value={currency.code}
              onChange={(v) => {
                const c = CURRENCIES.find(c => c.code === v);
                updateField('currency', { ...currency, code: v, symbol: c?.symbol || '$' });
              }}
              options={currencyOptions}
              placeholder="Search currencies..."
              testId="currency-code-select"
            />
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Currency Symbol</label>
              <input
                type="text"
                value={currency.symbol}
                onChange={(e) => updateField('currency.symbol', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                data-testid="currency-symbol-input"
              />
            </div>
            <SelectField
              label="Symbol Position"
              value={currency.position}
              onChange={(v) => updateField('currency.position', v)}
              options={[
                { value: 'before', label: 'Before amount ($100)' },
                { value: 'after', label: 'After amount (100$)' },
              ]}
              testId="currency-position-select"
            />
            <SelectField
              label="Decimal Places"
              value={String(currency.decimal_places ?? settings?.currency_decimal_places ?? 2)}
              onChange={(v) => updateField('currency.decimal_places', parseInt(v))}
              options={[
                { value: '0', label: '0 — 1,235' },
                { value: '2', label: '2 — 1,234.56' },
                { value: '3', label: '3 — 1,234.567' },
              ]}
              testId="currency-decimal-select"
            />
          </div>
          <div className="mt-3 p-2.5 rounded-lg bg-gray-50 border border-gray-100">
            <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">Preview</p>
            <p className="text-lg font-bold text-text-primary" data-testid="currency-preview">
              {(() => {
                const dp = currency.decimal_places ?? settings?.currency_decimal_places ?? 2;
                const formatted = (1234.567).toFixed(dp).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                return currency.position === 'before' ? `${currency.symbol}${formatted}` : `${formatted}${currency.symbol}`;
              })()}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Timezone & Date Format */}
      <Card className="bg-white border-gray-border" data-testid="timezone-settings">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-blue-600" />
            <CardTitle className="text-sm font-semibold">Timezone & Date Format</CardTitle>
            <Badge className="text-[9px] bg-blue-50 text-blue-600">{TIMEZONES.length} timezones</Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ComboSelect
              label="Timezone"
              value={settings?.timezone || 'America/New_York'}
              onChange={(v) => updateField('timezone', v)}
              options={timezoneOptions}
              placeholder="Search timezones..."
              testId="timezone-select"
            />
            <SelectField
              label="Date Format"
              value={settings?.date_format || 'MM/DD/YYYY'}
              onChange={(v) => updateField('date_format', v)}
              options={dateFormatOptions}
              testId="date-format-select"
            />
          </div>
          {settings?.timezone && (
            <div className="mt-3 p-2.5 rounded-lg bg-gray-50 border border-gray-100">
              <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">Current time in selected timezone</p>
              <p className="text-sm font-medium text-text-primary" data-testid="timezone-preview">
                {new Date().toLocaleString('en-US', { timeZone: settings.timezone, weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Language & Fiscal Year */}
      <Card className="bg-white border-gray-border" data-testid="fiscal-settings">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-purple-600" />
            <CardTitle className="text-sm font-semibold">Fiscal Year</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SelectField
              label="Fiscal Year Starts"
              value={settings?.fiscal_year_start || 'january'}
              onChange={(v) => updateField('fiscal_year_start', v)}
              options={fiscalOptions}
              testId="fiscal-year-select"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function countryCodeToFlag(code) {
  if (!code || code.length !== 2) return '';
  const offset = 127397;
  return String.fromCodePoint(...[...code.toUpperCase()].map(c => c.charCodeAt(0) + offset));
}
